import React, { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { useParams, useSearchParams } from 'react-router-dom'
import styled from '@emotion/styled'
import { LinearProgress } from '@mui/material'
import { H3 } from '../../../layouts/TextStyles'
import { studentApiSlice } from '../../../features/student/studentSlice'
import { groupApiSlice } from '../../../features/group/groupSlice'
import GroupStudentsTable from './components/GroupStudentsTable'
import AddGroupStudentDialog from './components/AddGroupStudentDialog'

interface Props {
    queryParams: any
}

const GroupStudents: React.FC<Props> = ({ queryParams = null }) => {
    const { formatMessage } = useIntl()
    const { groupId } = useParams()
    const [searchParams, setSearchParams] = useSearchParams('')
    const [studentsInfo, setStudentsInfo] = useState([])
    const [page, setPage] = React.useState(
        Number(searchParams.get('page')) || 1,
    )
    const [rowsPerPage, setRowsPerPage] = React.useState(
        Number(searchParams.get('size')) || 5,
    )
    const {
        data: groupStudentResponse = null,
        isFetching: isFetchingGroupStudents,
    } = groupApiSlice.useFetchAllGroupStudentsQuery({
        groupId: Number(groupId),
        params: `page=${page - 1}&size=${rowsPerPage}`,
    })

    const [getStudentsInfoByIds, { isLoading: isLoadingStudentsInfo }] =
        studentApiSlice.useGetStudentsInfoByIdsMutation()

    const getStudentsInfo = async () => {
        try {
            const result = await getStudentsInfoByIds(
                groupStudentResponse.data.map((student) => student.userId),
            ).unwrap()
            setStudentsInfo(result)
        } catch (error) {}
    }

    useEffect(() => {
        if (groupStudentResponse?.data?.length) {
            getStudentsInfo()
        }
    }, [groupStudentResponse?.data])

    useEffect(() => {
        const params = {
            course: queryParams.get('course'),
            groupName: queryParams.get('groupName'),
            page: page.toString(),
            size: rowsPerPage.toString(),
        }

        setSearchParams(params)
    }, [rowsPerPage, page])

    return (
        <Content>
            <Header>
                <Title>
                    <H3>
                        {searchParams.get('groupName')}{' '}
                        {formatMessage({ id: 'students' })}
                    </H3>
                </Title>

                <AddGroupStudentDialog />
            </Header>

            {isFetchingGroupStudents ? (
                <LinearProgress />
            ) : (
                groupStudentResponse && (
                    <GroupStudentsTable
                        studentsData={groupStudentResponse.data}
                        studentsInfo={studentsInfo}
                        isFetchingGroupStudents={isFetchingGroupStudents}
                        isLoadingStudentsInfo={isLoadingStudentsInfo}
                        pageNumber={groupStudentResponse.pageNumber}
                        totalCount={groupStudentResponse.totalCount}
                        page={page}
                        rowsPerPage={rowsPerPage}
                        setPage={setPage}
                        setRowsPerPage={setRowsPerPage}
                    />
                )
            )}
        </Content>
    )
}

export default GroupStudents

const Title = styled.div`
    margin-bottom: 20px;
    text-align: left;
    display: flex;
    align-items: center;
`

const Content = styled.div`
    background-color: #fff;
    border-radius: 16px;
    padding: 20px;
    min-height: 80vh;
`

const Header = styled.header`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
`
